import { createSelector } from 'reselect';
import _ from 'lodash';

import {
    selectorObtenirMunicipisEnCurs,
    selectorMunicipis
} from './selector_municipis';
import {
    selectorDataActual
} from './selector_data_actual';

import {
    rolsPermesosCrearAvis,
    rolsPermesosAssociablesAUnaEstacio
} from '../commons/callbacksSeguretat';

import {
    USUARI_FORMULARI_DADES_EDITADES_ESTAT_INICIAL
} from '../reducers/usuari/formulari/dades/editades';

import { ordenarAlfabeticamentPerAtributNom } from '../commons/ordenacio';

//Tots els usuari
export const selectorTotsUsuaris = state => state.usuari.tots;
export const selectorRols = state => state.usuari.rols;

const transformarRolsEnArrayOrdenatAlfabeticament = rols => {
    return Object.keys(rols)
        .map(idRolText => ({ id: Number(idRolText), nom: rols[idRolText]}))
        .sort(ordenarAlfabeticamentPerAtributNom);
};

export const selectorRolsOrdenatsAlfabeticament = createSelector(
    selectorRols,
    transformarRolsEnArrayOrdenatAlfabeticament
);

export const selectorPeticioInserirEnCurs = state => state.usuari.formulari.peticioEnviarFormulariEnCurs;

const afegirMunicipiComarcaIRolAUsuaris = (usuaris, rols, municipis) => {

    const idsUsuaris = Object.keys(usuaris);

    if (idsUsuaris <= 0) {
        return [];
    }

    return idsUsuaris.map(idUsuari => {

        const { codiINEMunicipi, idRol, cognom1, cognom2, ...usuariAmbAtributsFiltrats } = usuaris[idUsuari];

        const municipi = codiINEMunicipi && municipis ? municipis[codiINEMunicipi] : null;
        const nomRol = rols[idRol] || null;
        const nomMunicipi = municipi ? municipi.nom : null;
        const nomComarca = municipi && municipi.comarca ? municipi.comarca.nom : null;

        let cognoms = `${(cognom1 || '')} ${(cognom2 || '')}`;
        let cognomsSenseEspaisEnBlanc = cognoms.trim();

        return {...usuariAmbAtributsFiltrats, nomRol, nomMunicipi, nomComarca, cognoms: cognomsSenseEspaisEnBlanc};
    });

};

export const selectorTotsUsuarisAmbMunicipiIRol = createSelector(
    selectorTotsUsuaris,
    selectorRols,
    selectorMunicipis,
    afegirMunicipiComarcaIRolAUsuaris
);

const filtrarUsuarisPerRol = (usuaris, rolsAFiltrar) => {
    let usuarisObservadors = [];

    Object.keys(usuaris).forEach((idUsuari) => {
        const usuari = usuaris[idUsuari];
        if (rolsAFiltrar.indexOf(usuari.idRol) > -1){
            usuarisObservadors.push(usuari);
        }
    });

    return usuarisObservadors;
};

export const selectorObtenirUsuarisObservadors = createSelector(
    selectorTotsUsuaris,
    () => rolsPermesosAssociablesAUnaEstacio,
    filtrarUsuarisPerRol
);

export const selectorObtenirUsuarisVigilants = createSelector(
    selectorTotsUsuaris,
    () => rolsPermesosCrearAvis,
    filtrarUsuarisPerRol
);

const obtenirDadesOriginals = (dadesSeccio) => {
    return dadesSeccio.originals;
};

const obtenirDadesEditades = (dadesSeccio) => {
    return dadesSeccio.editades;
};

const dades = state => state.usuari.formulari.dades;

export const selectorFormulariDadesOriginals = createSelector(
    dades,
    obtenirDadesOriginals
);

export const selectorFormulariDadesEditades = createSelector(
    dades,
    obtenirDadesEditades
);

const obtenirDadesEstacions = (dadesSeccio) => {
    return dadesSeccio.estacions;
};

export const selectorFormulariDadesEstacions = createSelector(
    dades,
    obtenirDadesEstacions
);

const obtenirDadesEsActiu = (dadesSeccio) => {
    return dadesSeccio.esActiu;
};

export const selectorFormulariDadesEsActiu = createSelector(
    dades,
    obtenirDadesEsActiu
);

const selectorMostrarErrors = state => state.usuari.formulari.mostrarErrors;

const validarFormatCorreuElectronic = (email) => {
    const missatgeCorreuElectronicFormatIncorrecte = 'El correu electrònic ha de tenir un format vàlid';
    const expressioRegularEmail = /[^@]+@[^@]+\.[^@]+/;
    return email && !email.match(expressioRegularEmail) ? missatgeCorreuElectronicFormatIncorrecte : false;
};

const obtenirMissatgeErrorCampObligatori = (valor) => {
    const missatgeCampObligatori = 'Camp obligatori';
    return valor ? false : missatgeCampObligatori;
};

const obtenirMissatgeErrorCampNombreCaractersIncorrecte = (valor, nombreCaracters) => {
    const missatgeCampNoTeNombreCaractersCorrecte = `El camp ha de tenir ${nombreCaracters} caràcters`;
    return valor && valor.length !== nombreCaracters ? missatgeCampNoTeNombreCaractersCorrecte : false;
};

const obtenirMissatgeErrorDataAltaFutura = (dataActualText, dataAltaText) => {
    const missatgeDataAltaFutura = 'La data d\'alta no pot ser futura';
    return esDataFutura(dataActualText, dataAltaText) ? missatgeDataAltaFutura : false;
};

const obtenirMissatgeErrorDataBaixaFutura = (dataActualText, dataBaixaText) => {
    const missatgeDataBaixaFutura = 'La data de baixa no pot ser futura';
    return esDataFutura(dataActualText, dataBaixaText) ? missatgeDataBaixaFutura : false;
};

const esDataFutura = (dataActualText, dataText) => {
    const dataActualTextAmbFormat = dataActualText.substring(0, 10); // es trunca la dataActual a la màscara YYYY-MM-DD
    return (dataText > dataActualTextAmbFormat) ? true : false;
};

const obtenirMissatgeErrorDataAltaPosteriorDataBaixa = (dataAltaText, dataBaixaText) => {
    const missatgeDataAltaPosteriorDataBaixa = 'La data d\'alta no pot ser posterior a la data de baixa';
    return (dataAltaText > dataBaixaText) ? missatgeDataAltaPosteriorDataBaixa : false;
};

const obtenirMissatgeErrorDataBaixaAnteriorDataAlta = (dataAltaText, dataBaixaText) => {
    const missatgeDataBaixaAnteriorDataAlta = 'La data de baixa no pot ser anterior a la data d\'alta';
    return (dataBaixaText < dataAltaText) ? missatgeDataBaixaAnteriorDataAlta : false;
};

const obtenirMissatgeErrorCampDataAlta = (dataActualText, dataAlta, dataBaixa) => {
    return obtenirMissatgeErrorCampObligatori(dataAlta)
    || obtenirMissatgeErrorDataAltaFutura(dataActualText, dataAlta)
    || obtenirMissatgeErrorDataAltaPosteriorDataBaixa(dataAlta, dataBaixa);
};

const obtenirMissatgeErrorCampDataBaixa = (dataActualText, dataAlta, dataBaixa) => {
    return obtenirMissatgeErrorDataBaixaFutura(dataActualText, dataBaixa)
    || obtenirMissatgeErrorDataBaixaAnteriorDataAlta(dataAlta, dataBaixa);
};

const obtenirErrorsDades = (dataActualText, dadesEditades) => {
    const { nom, codi, telefon, idRol, codiINEMunicipi, codiPostal, dataAlta, dataBaixa, email } = dadesEditades;
    return {
        nom: obtenirMissatgeErrorCampObligatori(nom),
        codi: obtenirMissatgeErrorCampObligatori(codi),
        telefon: obtenirMissatgeErrorCampNombreCaractersIncorrecte(telefon, 9),
        idRol: obtenirMissatgeErrorCampObligatori(idRol),
        codiINEMunicipi: obtenirMissatgeErrorCampObligatori(codiINEMunicipi),
        codiPostal: obtenirMissatgeErrorCampNombreCaractersIncorrecte(codiPostal, 5),
        dataAlta: obtenirMissatgeErrorCampDataAlta(dataActualText, dataAlta, dataBaixa),
        dataBaixa: obtenirMissatgeErrorCampDataBaixa(dataActualText, dataAlta, dataBaixa),
        email: validarFormatCorreuElectronic(email)
    };
};

const obtenirErrorsCampsFormulari = createSelector(
    selectorDataActual,
    selectorFormulariDadesEditades,
    obtenirErrorsDades
);

const errorsCampsFormulari = (mostrarErrors, errorsFormulari) => mostrarErrors ? errorsFormulari : {};

export const selectorFormulariErrors = createSelector(
    selectorMostrarErrors,
    obtenirErrorsCampsFormulari,
    errorsCampsFormulari
);

const hiHaErrorsCampsFormulari = errorsCamps => Object.keys(errorsCamps).some(idCamp => errorsCamps[idCamp] !== false);

export const selectorFormulariHiHaErrorsCamps = createSelector(
    obtenirErrorsCampsFormulari,
    hiHaErrorsCampsFormulari
);

const obtenirUsuarisEnCurs = state => state.usuari.peticioUsuarisEnCurs;
const obtenirRolsEnCurs = state => state.usuari.peticioRolsEnCurs;

export const selectorUsuariOnEnterEnCurs = createSelector(
    obtenirUsuarisEnCurs,
    obtenirRolsEnCurs,
    selectorObtenirMunicipisEnCurs,
    (...args) => args.some(peticioEnCurs => peticioEnCurs === true)
);

export const selectorHiHaEstructuraFormulariUsuari = state => {
    const rols = selectorRols(state);
    const municipis = selectorMunicipis(state);
    return municipis !== null && Object.keys(municipis).length > 0 && Object.keys(rols).length > 0 ;
};

export const selectorFormulariObtenirDadesEnCurs = state => state.usuari.formulari.peticioObtenirDadesEnCurs;

export const selectorFormulariIdUsuariSeleccionat = (state) => state.usuari.formulari.idUsuariSeleccionat;

export const selectorFormulariNoShanModificatDades = createSelector(
    selectorFormulariDadesOriginals,
    selectorFormulariDadesEditades,
    _.isEqual
);

export const selectorPerfilOnEnterEnCurs = createSelector(
    selectorFormulariObtenirDadesEnCurs,
    obtenirRolsEnCurs,
    selectorObtenirMunicipisEnCurs,
    (...args) => args.some(peticioEnCurs => peticioEnCurs === true)
);

const hiHaDadesFormulariUsuari = createSelector(
    selectorFormulariDadesEditades,
    dadesEditades => !_.isEqual(dadesEditades, USUARI_FORMULARI_DADES_EDITADES_ESTAT_INICIAL)
);

export const selectorHiHaDadesNecessariesPelPerfil = createSelector(
    selectorHiHaEstructuraFormulariUsuari,
    hiHaDadesFormulariUsuari,
    (...args) => args.every(hiHaDades => hiHaDades === true)
);



// WEBPACK FOOTER //
// ./src/selectors/selector_usuari.js